import { useCallback, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { authenticationState, countryState, customerState } from '@repo/unicity'
import { getPaymentResponseGatewayUrls } from '@/core/env.config'
import {
    omanFormatDataAtom,
    omanFormatRawAtom,
    omanSavedCardsAtom,
    omanCheckoutLoadingAtom,
    omanCheckoutErrorsAtom,
    omanProcessingPaymentAtom,
    omanProcessingPaymentModalOpenAtom,
    omanDuplicateOrderAtom,
    omanSelectedShippingMethodAtom,
    initialOmanProcessingPayment
} from '../stores/oman.store'
import {
    deleteOmanSavedCard,
    fetchOmanSavedCards,
    saveCardAlias,
    checkCardAlias,
    updateUuidWithAlias,
    createPaymentV3,
    pollPaymentStatusV3,
    submitGatewayForm,
    checkDuplicateOrderV3,
    validateOmanCreditCard
} from '../services/oman.api'
import { trackPurchaseEvent } from '../utils/tracking'
import { extractErrorMessage, resolveTerminal, toFourDigitYear, buildBillingInfo, resolvePayerName, type BillingInfo } from '../utils/oman-payment.helpers'
import {
    PAYMENTS_V3_METHOD,
    type TOmanCreditCardForm,
    type TOmanSaveCardAliasPayload,
    type TOmanCreditcardAlias,
    type PaymentsV3PaymentItem,
    type PaymentsV3BillToAddress,
    type PaymentsV3Method,
    type ProcessingPaymentState
} from '../types/oman.types'
import { encryptCreditCard } from '../utils/encryptCreditCard'
import { analytics, type AnalyticsProps } from '@/core/analytics/analytics'
import { newAttemptId, rememberAttempt } from '@/core/analytics/checkout-attempt'

const MAX_POLL_MS = 5 * 60 * 1000
const DEFAULT_POLL_INTERVAL_MS = 1500
// Give up after this many *consecutive* poll failures instead of retrying a
// dead endpoint (expired session, gone ref id) for the full MAX_POLL_MS window.
const MAX_POLL_ERRORS = 5
const SAVE_CARD_ALIAS_ERROR = 'failed_to_save_card_alias'

// State used after a top-level failure (createPaymentV3 threw / unknown
// next_action). Payment never reached the gateway — both payment and hydra
// steps are marked `fail` so the modal stepper shows red crosses for both
// rows instead of leaving `prepare_data` spinning.
const PRE_GATEWAY_FAIL_STATE: ProcessingPaymentState = {
    status: 'error',
    payment_status: 'fail',
    hydra_status: 'fail'
}

const checkCreditCardAccess = async (baId: string, cardNumber: string) => {
    try {
        const result = await validateOmanCreditCard(baId, normalizeCardNumber(cardNumber).slice(0, 6))
        if (result.allowAccess === false) throw new Error('credit_card_has_been_blocked')
        if (result.isAllowedIssuingNetwork === false) throw new Error('err_not_supported_credit_card_type')
    } catch (err) {
        if (err instanceof Error && (err.message === 'credit_card_has_been_blocked' || err.message === 'err_not_supported_credit_card_type')) {
            throw err
        }
    }
}

const normalizeCardNumber = (cardNumber: string) => cardNumber.replace(/\D/g, '')

const buildCreditCardExpires = (cardForm: TOmanCreditCardForm) => `${toFourDigitYear(cardForm.expYear)}-${cardForm.expMonth}`

const buildSaveCardAliasPayload = ({
    cardForm,
    billingInfo,
    payerName
}: {
    cardForm: TOmanCreditCardForm
    billingInfo: BillingInfo
    payerName: string
}): TOmanSaveCardAliasPayload => {
    const rawCardNumber = normalizeCardNumber(cardForm.cardNumber)
    const { billing, email, phone, name } = billingInfo

    return {
        methodDetails: {
            alias: rawCardNumber.slice(0, 6) + 'xxxxxx' + rawCardNumber.slice(-4),
            creditCardNumber: rawCardNumber,
            creditCardExpires: buildCreditCardExpires(cardForm),
            payer: payerName
        },
        billToAddress: {
            country: billing.country || 'OM',
            address1: billing.address1,
            address2: billing.address2,
            city: billing.city,
            state: billing.state
        },
        billToEmail: email,
        billToPhone: phone,
        billToName: { firstName: name.firstName, lastName: name.lastName, fullName: payerName }
    }
}

const saveAndAttachCardAlias = async ({
    payload,
    uuid,
    token,
    customerCountry
}: {
    payload: TOmanSaveCardAliasPayload
    uuid: string
    token: string
    customerCountry: string
}) => {
    const saveRes = await saveCardAlias(payload, token, customerCountry).catch(() => {
        throw new Error(SAVE_CARD_ALIAS_ERROR)
    })
    const { data: aliasData, headers: aliasHeaders } = await checkCardAlias(saveRes.href, token)
    // `hydra_alias_detail_curl` is intentionally blank — the legacy contract
    // echoes Hydra's response headers + body back to format service.
    await updateUuidWithAlias(uuid, '', JSON.stringify(aliasHeaders), JSON.stringify(aliasData), token)
}

export const useOmanPayment = () => {
    const formatData = useAtomValue(omanFormatDataAtom)
    const formatRaw = useAtomValue(omanFormatRawAtom)
    const authentication = useAtomValue(authenticationState)
    const customer = useAtomValue(customerState)
    const country = useAtomValue(countryState)
    const shippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    // Hydra stores credit-card aliases under the *customer's* home market (a
    // Thai customer buying for the Oman build still has aliases under `/TH`).
    // Always derive the URL country from `customer.mainAddress.country` and
    // only fall back to the build country when the customer record is empty.
    const customerCountry = customer?.mainAddress?.country || country.shorter
    const [savedCards, setSavedCards] = useAtom(omanSavedCardsAtom)
    const [loading, setLoading] = useAtom(omanCheckoutLoadingAtom)
    const [errors, setErrors] = useAtom(omanCheckoutErrorsAtom)
    const setProcessingPayment = useSetAtom(omanProcessingPaymentAtom)
    const setModalOpen = useSetAtom(omanProcessingPaymentModalOpenAtom)
    const setDuplicateOrder = useSetAtom(omanDuplicateOrderAtom)
    const navigate = useNavigate()
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // ─── Session helpers ────────────────────────────────────────────────────────

    const hasSubscriptionItems = () => {
        if (formatData?.items?.length) {
            return formatData.items.some(item => item.type === 'subscription')
        }
        return formatData?.hasSubscription ?? false
    }

    const getSession = () => {
        const uuid = formatData?.uuid
        const token = authentication.token
        const baId = authentication.baId
        if (!uuid || !token || !baId) return null
        return { uuid, token, baId, hasSubscription: hasSubscriptionItems() }
    }

    const requireSession = () => {
        const session = getSession()
        if (!session) {
            if (!authentication.token || !authentication.baId) window.location.replace('/')
            throw new Error('Session expired. Please restart checkout.')
        }
        return session
    }

    // ─── Tracing ───────────────────────────────────────────────────────────────
    //
    // One id per pay attempt, stamped on every event from purchaseClicked onward, so a whole
    // journey (click → create → next_action → polling → result) is one filterable trace —
    // including the branches that fail before a gateway_ref_id exists.
    const attemptIdRef = useRef('')
    const attemptMethodRef = useRef<PaymentsV3Method | ''>('')

    const trackCheckout = (event: Parameters<typeof analytics.track>[0], props?: AnalyticsProps) =>
        analytics.track(event, {
            checkout_attempt_id: attemptIdRef.current || undefined,
            payment_method: attemptMethodRef.current || undefined,
            shipping_method: shippingMethod,
            cart_item_count: formatData?.items?.length ?? 0,
            has_subscription: hasSubscriptionItems(),
            ...props
        })

    const beginPaymentAttempt = (method: PaymentsV3Method) => {
        attemptIdRef.current = newAttemptId()
        attemptMethodRef.current = method
        trackCheckout('checkoutPurchaseClicked')
    }

    // ─── Processing modal ──────────────────────────────────────────────────────

    const openProcessingPaymentModal = () => {
        // Step defaults come from `initialOmanProcessingPayment` so the stepper
        // shows spinners immediately while createPaymentV3() is in-flight.
        setProcessingPayment({ ...initialOmanProcessingPayment, status: 'submitting' })
        setModalOpen(true)
    }

    const closeProcessingPaymentModal = () => {
        setModalOpen(false)
        setProcessingPayment(initialOmanProcessingPayment)
        setLoading(false)
    }

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current)
            pollTimerRef.current = null
        }
    }

    const navigateToSuccess = (gatewayRefId: string) => {
        if (formatData) trackPurchaseEvent(gatewayRefId, formatData)
        setTimeout(() => {
            closeProcessingPaymentModal()
            navigate(`/payment-response/success?gateway_ref_id=${gatewayRefId}`)
        }, 800)
    }

    // ─── Polling ────────────────────────────────────────────────────────────────

    const startPolling = (gatewayRefId: string, intervalMs: number) => {
        const startedAt = Date.now()
        let consecutiveErrors = 0
        setProcessingPayment(prev => ({ ...prev, status: 'processing' }))
        trackCheckout('checkoutPaymentPollingStarted', { gateway_ref_id: gatewayRefId, interval_ms: intervalMs })

        const poll = async () => {
            if (Date.now() - startedAt > MAX_POLL_MS) {
                stopPolling()
                trackCheckout('checkoutPaymentResultReceived', { gateway_ref_id: gatewayRefId, result: 'max_timeout' })
                setProcessingPayment({ status: 'error', payment_status: 'fail', hydra_status: 'timeout' })
                return
            }

            try {
                const res = await pollPaymentStatusV3(gatewayRefId)
                consecutiveErrors = 0
                // Each step mirrors the API field directly — STEP_STYLE in the
                // modal handles every possible value. A single tick can report
                // `payment_status:'fail'` together with `hydra_status:'fail'`,
                // so never infer one step from the other.
                const terminal = resolveTerminal(res)

                if (terminal === 'success') {
                    stopPolling()
                    trackCheckout('checkoutPaymentResultReceived', {
                        gateway_ref_id: gatewayRefId,
                        result: 'success',
                        payment_status: res.payment_status,
                        hydra_status: res.hydra_status
                    })
                    setProcessingPayment({ status: 'success', payment_status: 'success', hydra_status: 'success' })
                    navigateToSuccess(gatewayRefId)
                    return
                }

                if (terminal === 'error') {
                    stopPolling()
                    // Charged but Hydra never produced the order: retrying double-charges, so
                    // this one needs to be findable by gateway_ref_id from a support ticket.
                    if (res.payment_status === 'success') {
                        trackCheckout('checkoutPaidNoOrderDetected', { gateway_ref_id: gatewayRefId, hydra_status: res.hydra_status })
                    }
                    trackCheckout('checkoutPaymentResultReceived', {
                        gateway_ref_id: gatewayRefId,
                        result: 'error',
                        payment_status: res.payment_status,
                        hydra_status: res.hydra_status
                    })
                    setProcessingPayment({
                        status: 'error',
                        payment_status: res.payment_status,
                        hydra_status: res.hydra_status,
                        hydra_response_body: res.hydra_response_body
                    })
                    return
                }

                setProcessingPayment({
                    status: 'processing',
                    payment_status: res.payment_status,
                    hydra_status: res.hydra_status
                })
            } catch {
                // Transient network blip — retry a few times, then stop so a dead
                // endpoint (expired session / gone ref id) surfaces an error instead
                // of spinning for the full MAX_POLL_MS window.
                if (++consecutiveErrors >= MAX_POLL_ERRORS) {
                    stopPolling()
                    trackCheckout('checkoutPaymentResultReceived', { gateway_ref_id: gatewayRefId, result: 'poll_error_cap', poll_errors: consecutiveErrors })
                    setProcessingPayment({ status: 'error', payment_status: 'fail', hydra_status: 'fail' })
                    return
                }
            }

            pollTimerRef.current = setTimeout(poll, intervalMs)
        }

        poll()
    }

    // ─── Handle next_action response ────────────────────────────────────────────

    const handleNextAction = (response: Awaited<ReturnType<typeof createPaymentV3>>) => {
        const { next_action, gateway_ref_id, poll_interval_ms, gateway_data } = response
        const interval = poll_interval_ms ?? DEFAULT_POLL_INTERVAL_MS

        rememberAttempt(gateway_ref_id, attemptIdRef.current)
        trackCheckout('checkoutPaymentNextActionReceived', { gateway_ref_id, next_action })

        switch (next_action) {
            case 'polling':
            case 'qr_display':
            case 'createOrderViaFE':
                // V3 backend may return formatHydra under `createOrderViaFE`,
                // but for Oman the expected routing is polling or redirect —
                // treat it as polling.
                startPolling(gateway_ref_id, interval)
                break

            case 'gateway_redirect':
                if (gateway_data) {
                    // Keep the processing modal (spinner) up through the redirect. Closing
                    // it first leaves the page looking idle during the gap before the bank
                    // (e.g. Bank Muscat) loads, and re-enables Confirm underneath — inviting a
                    // duplicate submit. The bank navigation unmounts the modal (USHOP-3078).
                    submitGatewayForm(gateway_data as Record<string, string | undefined>)
                }
                break

            case 'redirect_url':
                if (gateway_data?.redirect_url) {
                    // Same as gateway_redirect: hold the spinner until the browser navigates.
                    window.location.href = gateway_data.redirect_url
                }
                break

            default:
                trackCheckout('checkoutPaymentCreateFailed', { gateway_ref_id, reason: 'unsupported_next_action', next_action: String(next_action) })
                setProcessingPayment(PRE_GATEWAY_FAIL_STATE)
        }
    }

    /**
     * Common create+handle wrapper used by every payment method except the
     * credit-card form (which has extra alias-saving steps before the create
     * call). Opens the processing modal, calls createPaymentV3 and routes the
     * response through `handleNextAction`. On failure, marks the payment step
     * as failed and rethrows for the caller's setErrors().
     */
    const runPayment = async (uuid: string, token: string, payment: PaymentsV3PaymentItem) => {
        trackCheckout('checkoutPaymentCreateStarted', { uuid })
        try {
            const response = await createPaymentV3(
                {
                    uuid,
                    source_path: window.location.href,
                    payments: [payment],
                    ...getPaymentResponseGatewayUrls()
                },
                token
            )

            // Some pre-gateway rejections still come back with HTTP 200 but
            // `success: false` in the body. Surface the message through the
            // same catch path so the modal renders it via ErrorMessages.
            if (response.success === false) {
                throw new Error(response.error?.message || 'Payment failed. Please try again.')
            }

            handleNextAction(response)
        } catch (err) {
            // Pre-gateway failure — axios throws on non-2xx with the API's
            // `{ error: { message } }` payload (extracted by the caller via
            // `extractErrorMessage`). Keep the processing modal open in the
            // failure state so it can render the ErrorMessages box inside.
            // Real values, not booleans: log_id is the handle for the backend log of this
            // exact failure, and the http status says what actually broke.
            const data = (err as { response?: { data?: { log_id?: string; error?: { message?: string; httpStatus?: number } } } })?.response?.data
            trackCheckout('checkoutPaymentCreateFailed', {
                uuid,
                reason: 'create_payment',
                log_id: data?.log_id,
                error_code: data?.error?.message ?? (err instanceof Error ? err.message : undefined),
                http_status: data?.error?.httpStatus
            })
            setProcessingPayment(PRE_GATEWAY_FAIL_STATE)
            throw err
        }
    }

    // ─── Pre-payment checks ────────────────────────────────────────────────────

    const checkDuplicate = useCallback(async (): Promise<'proceed' | 'duplicate' | 'error'> => {
        const session = getSession()
        if (!session) {
            if (!authentication.token || !authentication.baId) window.location.replace('/')
            return 'error'
        }
        try {
            const result = await checkDuplicateOrderV3(session.uuid)
            if (result.is_duplicate) {
                trackCheckout('checkoutDuplicateDetected', {
                    uuid: session.uuid,
                    gateway_ref_id: result.gateway_ref_id ?? '',
                    is_waiting: result.is_waiting ?? false
                })
                setDuplicateOrder(result)
                return 'duplicate'
            }
            return 'proceed'
        } catch {
            return 'proceed'
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps already list every reactive value the (unmemoized) session/billing/runPayment helpers read
    }, [formatData, authentication, setDuplicateOrder])

    // ─── Billing-address overrides for cc / cc-alias ───────────────────────────
    //
    // Per PaymentsV3 docs:
    //   - Default delivery with same-address: send
    //     `isBillAddressSameShipToAddress: true` so BE copies billing from ship-to.
    //   - Delivery with a separate billing address, and all pickup orders:
    //     send `isBillAddressSameShipToAddress: false` with `billToAddress`.
    const buildBillingFields = (
        cardForm?: TOmanCreditCardForm
    ): {
        isBillAddressSameShipToAddress: boolean
        billToAddress?: PaymentsV3BillToAddress
    } => {
        if (shippingMethod !== 'pickup' && cardForm?.billingSameAsShipping !== false) {
            return { isBillAddressSameShipToAddress: true }
        }

        const fallbackForm: TOmanCreditCardForm = cardForm ?? {
            cardNumber: '',
            expMonth: '',
            expYear: '',
            cvv: '',
            saveCard: false,
            billingSameAsShipping: true,
            billing: { firstname: '', country: 'OM', address1: '', address2: '', city: '', state: '' }
        }
        const { billing, email, phone, name } = buildBillingInfo({
            cardForm: fallbackForm,
            formatData,
            formatRaw,
            customer
        })
        const billingFullName = [name.firstName, name.lastName].filter(Boolean).join(' ')

        return {
            isBillAddressSameShipToAddress: false,
            billToAddress: {
                email: email || formatData?.email || '',
                phone: phone || formatData?.phone || '',
                fullname: billingFullName,
                address: billing.address1 ?? '',
                address2: billing.address2,
                city: billing.city ?? '',
                state: billing.state,
                country: billing.country || 'OM'
            }
        }
    }

    // ─── Credit card payment (V3) ──────────────────────────────────────────────

    const submitCreditCardPayment = useCallback(
        async (cardForm: TOmanCreditCardForm) => {
            const { uuid, token, baId, hasSubscription } = requireSession()
            beginPaymentAttempt(PAYMENTS_V3_METHOD.creditCard)
            if (hasSubscription || normalizeCardNumber(cardForm.cardNumber)) {
                // Blocked card / unsupported network throws here, before any create call — the
                // shopper sees a message and, without this, the trace ends at purchaseClicked.
                await checkCreditCardAccess(baId, cardForm.cardNumber).catch((err: unknown) => {
                    trackCheckout('checkoutPurchaseValidationError', { reason: err instanceof Error ? err.message : 'card_access' })
                    throw err
                })
            }

            const billingInfo = buildBillingInfo({
                cardForm,
                formatData,
                formatRaw,
                customer
            })
            const { name } = billingInfo
            const payerName = resolvePayerName({ customer, formatData, fallback: name.firstName })

            // Save card alias if subscription or user opted in
            if (hasSubscription || cardForm.saveCard) {
                await saveAndAttachCardAlias({
                    payload: buildSaveCardAliasPayload({ cardForm, billingInfo, payerName }),
                    uuid,
                    token,
                    customerCountry
                }).catch((err: unknown) => {
                    trackCheckout('checkoutPurchaseValidationError', { reason: err instanceof Error ? err.message : SAVE_CARD_ALIAS_ERROR })
                    throw err
                })
            }

            openProcessingPaymentModal()

            const payment: PaymentsV3PaymentItem = {
                method: PAYMENTS_V3_METHOD.creditCard,
                ...buildBillingFields(cardForm)
            }

            if (hasSubscription) {
                payment.payer = payerName
            }

            if (hasSubscription && shippingMethod === 'delivery') {
                payment.ccEncrypted = await encryptCreditCard(normalizeCardNumber(cardForm.cardNumber), buildCreditCardExpires(cardForm), cardForm.cvv)
            }

            await runPayment(uuid, token, payment)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps already list every reactive value the (unmemoized) session/billing/runPayment helpers read
        [formatData, formatRaw, authentication, customer, customerCountry, shippingMethod, navigate]
    )

    // ─── Saved card alias payment (V3) ─────────────────────────────────────────

    const submitAliasPayment = useCallback(
        async (aliasId: string) => {
            const { uuid, token } = requireSession()
            beginPaymentAttempt(PAYMENTS_V3_METHOD.creditCardAlias)
            openProcessingPaymentModal()
            await runPayment(uuid, token, {
                method: PAYMENTS_V3_METHOD.creditCardAlias,
                aliasName: aliasId,
                ...buildBillingFields()
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps already list every reactive value the (unmemoized) session/billing/runPayment helpers read
        [formatData, formatRaw, authentication, customer, shippingMethod, navigate]
    )

    // ─── Bank wire payment (V3) ────────────────────────────────────────────────

    const submitBankWirePayment = useCallback(async () => {
        const { uuid, token } = requireSession()
        beginPaymentAttempt(PAYMENTS_V3_METHOD.bankWire)
        openProcessingPaymentModal()
        await runPayment(uuid, token, { method: PAYMENTS_V3_METHOD.bankWire })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps already list every reactive value the (unmemoized) session/billing/runPayment helpers read
    }, [formatData, authentication, navigate])

    // ─── Cash on Delivery / Pickup payment (V3) ────────────────────────────────

    const submitCashPayment = useCallback(
        async (method: PaymentsV3Method = PAYMENTS_V3_METHOD.cod) => {
            const { uuid, token } = requireSession()
            beginPaymentAttempt(method)
            openProcessingPaymentModal()
            await runPayment(uuid, token, { method })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps already list every reactive value the (unmemoized) session/billing/runPayment helpers read
        [formatData, authentication, navigate]
    )

    // ─── Delete saved card ─────────────────────────────────────────────────────

    const deleteCard = useCallback(
        async (card: TOmanCreditcardAlias) => {
            if (!authentication.token || !authentication.baId) {
                window.location.replace('/')
                return
            }
            await deleteOmanSavedCard(card.methodDetails.alias, authentication.token, customerCountry)
            try {
                const refreshed = await fetchOmanSavedCards(authentication.baId, authentication.token, customerCountry)
                setSavedCards(refreshed.items ?? [])
            } catch {
                // Delete succeeded but the refetch failed — drop just the deleted card
                // from the current list rather than wiping every saved card the user has.
                setSavedCards(prev => prev.filter(c => c.methodDetails.alias !== card.methodDetails.alias))
            }
        },
        [authentication.token, authentication.baId, customerCountry, setSavedCards]
    )

    return {
        loading,
        errors,
        setErrors,
        setLoading,
        savedCards,
        submitCreditCardPayment,
        submitAliasPayment,
        submitBankWirePayment,
        submitCashPayment,
        deleteCard,
        checkDuplicate,
        closeProcessingPaymentModal,
        stopPolling,
        extractErrorMessage
    }
}
